import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import { BlockTitle, SelectMachineForJobLine } from '@v2/pages/machines/modules/now/components/Job/components/JobContent';
import TouchableElement from '@components/common/touchableElement';
import { Period } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';

import { injectConditionalArrayItems, isPhone, isWeb } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import {
  getJobOrOrderStatusConfig,
  LINE_HORIZONTAL_PADDING_WITH_ACTION_ARROW,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
} from '../../../../../v2/pages/common/constants/base.constants';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { OperationSearchItem } from '@interfaces/search';
import { operationsSearch } from '@reducers/search';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import StatusBadge from '@components/common/StatusBadge';
import { useManageJobContent } from './hooks/useManageJobContent';

export interface JobContentProps extends WithColor {
  job?: OperationSearchItem;
  operationsSearch: (data: any, filter?: any, select?: any, _?: any, resetHistory?: any, signal?: any) => Promise<any>;
  // Optimistic local-state patch after FinishJobModal succeeds — OP owns jobs in useState, not Redux.
  onJobFinished?: (productionOperationId: number) => void;
}

const JobContent: React.FC<JobContentProps> = (props) => {
  const { job, C, operationsSearch, onJobFinished } = props;

  const {
    similarJobsCount,
    similarJobsQuantity,
    containerWidth,
    setContainerWidth,
    isMachinePerspective,
    selectedMachineForJob,
    machineByJobStatus,
    machineName,
    showMarkAsFinished,
    toggleMachines,
    handleMarkAsFinished,
  } = useManageJobContent({ job, operationsSearch, onJobFinished, scale: scaleOP, font: fontOP });

  if (!job) {
    return null;
  }

  const styles = [
    {
      marginBottom: scaleOP(isPhone ? 15 : 18),
      borderRadius: scaleOP(SCREEN_BORDER_RADIUS),
    },
  ];

  const leftBlockTiles = [
    {
      content: [
        {
          borderBottomRadius: false,
          borderTopRadius: true,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Operation',
            value: job?.operationName,
            isText: true,
          },
          layoutForPhone: true,
          rightSideTakesAllSpace: true,
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: machineByJobStatus?.label ?? 'Machine',
            ...(machineName || selectedMachineForJob?.name
              ? { value: selectedMachineForJob?.name || machineName }
              : { ValueComponent: <SelectMachineForJobLine C={C} scale={scaleOP} font={fontOP} /> }),
            action: toggleMachines,
          },
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Ready',
            value: `${job.quantityProduced} / ${job.quantityTarget} ${i18n.t('Pcs')}`,
          },
        },
        {
          borderBottomRadius: !showMarkAsFinished,
          borderTopRadius: false,
          onlyLabel: false,
          data: showMarkAsFinished ? [] : [{}],
          index: 0,
          i: {
            name: 'Status',
            ValueComponent: <StatusBadge C={C} config={getJobOrOrderStatusConfig(job.status, C)} font={fontOP} scale={scaleOP} />,
          },
        },
        ...injectConditionalArrayItems(showMarkAsFinished, {
          borderBottomRadius: true,
          borderTopRadius: false,
          onlyLabel: true,
          data: [{}],
          index: 0,
          i: {
            name: 'markAsFinished',
            action: handleMarkAsFinished,
            Component: () => (
              <Text numberOfLines={1} style={[{ color: C('Link/Light/Primary') }, fontOP.Body]}>
                {i18n.t('Mark As Finished...')}
              </Text>
            ),
          },
          testID: 'op-jobs-mark-as-finished',
        }),
      ],
    },
    {
      title: 'SCHEDULE',
      content: [
        {
          condition: !!job.actualStartDate,
          borderBottomRadius: false,
          borderTopRadius: true,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Actual Start',
            ValueComponent: <Period startedOn={job.actualStartDate ?? ''} C={C} font={fontOP} />,
          },
        },
        {
          condition: !!job.actualEndDate,
          borderBottomRadius: false,
          borderTopRadius: !job.actualStartDate,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Actual End',
            ValueComponent: <Period startedOn={job.actualEndDate ?? ''} C={C} font={fontOP} />,
          },
        },
        {
          borderBottomRadius: false,
          borderTopRadius: !job.actualStartDate && !job.actualEndDate,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Scheduled Start',
            ValueComponent: <Period startedOn={job.scheduledStartDate ?? ''} C={C} font={fontOP} />,
          },
        },
        {
          borderBottomRadius: true,
          borderTopRadius: false,
          onlyLabel: false,
          data: [{}],
          index: 0,
          i: {
            name: 'Scheduled End',
            ValueComponent: <Period startedOn={job.scheduledEndDate ?? ''} C={C} font={fontOP} />,
          },
        },
      ],
    },
  ];

  const rightBlockTiles = [
    {
      component: (
        <ImagesContainer
          {...{
            bg: 'Background/Light/Field on Panel',
            maxDetailsWidth: '100%',
            panelViewMode: false,
            mode: ImagesContainerMode.ROW,
            documentRetrieveKey: DOCUMENT_ENTITY_TYPE.ProductionOperation,
            documentRetrieveId: job?.productionOperationId,
            retrieveAllDocuments: true,
            containerBorderRadiusConfig: {
              borderBottomLeftRadius: scaleOP(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scaleOP(SCREEN_BORDER_RADIUS),
            },
            noBorder: true,
            scale: scaleOP,
            font: fontOP,
          }}
        />
      ),
    },
    {
      title: 'INSTRUCTION',
      content: [
        {
          borderBottomRadius: true,
          borderTopRadius: true,
          onlyLabel: true,
          data: [{}],
          index: 0,
          i: {
            name: job.description || '-',
          },
        },
      ],
    },
    {
      content: [
        {
          borderBottomRadius: false,
          borderTopRadius: true,
          onlyLabel: false,
          data: [],
          index: 0,
          layoutForPhone: true,
          rightSideTakesAllSpace: true,
          i: {
            isText: true,
            name: 'Description',
            value: job.partDescription,
          },
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Part',
            value: job.partNumber,
          },
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Drawing',
            value: `${job.partDrawingNumber ?? ''} ${job.partDrawingPosition ?? ''} ${job.partDrawingRevision ?? ''}`,
          },
        },
        {
          borderBottomRadius: true,
          borderTopRadius: false,
          onlyLabel: false,
          data: [{}],
          index: 0,
          i: {
            name: 'Customer',
            value: job.customerName,
          },
        },
      ],
    },
    {
      title: 'SIMILAR JOBS',
      content: [
        {
          borderBottomRadius: true,
          borderTopRadius: true,
          onlyLabel: false,
          data: [{}],
          index: 0,
          i: {
            Component: () => {
              return (
                <TouchableElement onPress={console.log}>
                  {({ boxStyle }) => {
                    return (
                      <View
                        style={[
                          boxStyle,
                          {
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          },
                        ]}
                      >
                        <Text numberOfLines={1} style={[{ color: C('Label/Light/Primary') }, fontOP.Body]}>
                          {i18n.t('Total In Production')}
                        </Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', height: '100%' }}>
                          <Text
                            numberOfLines={1}
                            style={[
                              {
                                color: C('Label/Light/Tertiary'),
                                textAlign: 'right',
                                paddingRight: scaleOP(LINE_HORIZONTAL_PADDING_WITH_ACTION_ARROW),
                                alignSelf: 'center',
                                border: 'none',
                                outline: 'none',
                              },
                              fontOP.Body,
                            ]}
                          >
                            {`${similarJobsCount} (${similarJobsQuantity} ${i18n.t(isMachinePerspective ? 'Jobs' : 'Job')})`}
                          </Text>
                          {/* <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scaleOP(7)} height={scaleOP(12)} /> */}
                        </View>
                      </View>
                    );
                  }}
                </TouchableElement>
              );
            },
          },
        },
        // {
        //   borderBottomRadius: true,
        //   borderTopRadius: false,
        //   onlyLabel: false,
        //   data: [{}],
        //   index: 0,
        //   i: {
        //     Component: () => {
        //       return (
        //         <TouchableElement onPress={console.log}>
        //           {({ boxStyle }) => {
        //             return (
        //               <View
        //                 style={[
        //                   boxStyle,
        //                   {
        //                     width: '100%',
        //                     display: 'flex',
        //                     flexDirection: 'row',
        //                     alignItems: 'center',
        //                     justifyContent: 'space-between',
        //                   },
        //                 ]}
        //               >
        //                 <Text numberOfLines={1} style={[{ color: C('Label/Light/Primary') }, fontOP.Body]}>
        //                   {i18n.t('Repeat Count')}
        //                 </Text>
        //                 <View style={{ flexDirection: 'row', alignItems: 'center', height: '100%' }}>
        //                   <Text
        //                     numberOfLines={1}
        //                     style={[
        //                       {
        //                         color: C('Label/Light/Tertiary'),
        //                         textAlign: 'right',
        //                         paddingRight: scaleOP(LINE_HORIZONTAL_PADDING_WITH_ACTION_ARROW),
        //                         alignSelf: 'center',
        //                         border: 'none',
        //                         outline: 'none',
        //                       },
        //                       fontOP.Body,
        //                     ]}
        //                   >
        //                     {repeatCount}
        //                   </Text>
        //                   <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scaleOP(7)} height={scaleOP(12)} />
        //                 </View>
        //               </View>
        //             );
        //           }}
        //         </TouchableElement>
        //       );
        //     },
        //   },
        // },
      ],
    },
    {
      title: isMachinePerspective ? 'TIMES' : 'SCHEDULED TIMES',
      content: [
        {
          borderBottomRadius: false,
          borderTopRadius: true,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Total',
            ValueComponent: (
              <TimeDisplayInStaticFormat
                C={C}
                timeValue={job.totalTimeScheduledSeconds ?? 0}
                fontForDigit="Body"
                fontForSecondDigit="Body"
                scale={scaleOP}
                font={fontOP}
              />
            ),
          },
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Setup',
            ValueComponent: (
              <TimeDisplayInStaticFormat
                C={C}
                timeValue={job.setupTimeSeconds ?? 0}
                fontForDigit="Body"
                fontForSecondDigit="Body"
                scale={scaleOP}
                font={fontOP}
              />
            ),
          },
        },
        {
          borderBottomRadius: true,
          borderTopRadius: false,
          onlyLabel: false,
          data: [{}],
          index: 0,
          i: {
            name: 'Per Piece',
            ValueComponent: (
              <TimeDisplayInStaticFormat
                C={C}
                timeValue={job.operationTimeSeconds ?? 0}
                fontForDigit="Body"
                fontForSecondDigit="Body"
                scale={scaleOP}
                font={fontOP}
              />
            ),
          },
        },
      ],
    },
  ];

  const backgroundColor = 'Background/Light/Field on Panel';
  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View
        style={{
          flexDirection: 'row',
          gap: scaleOP(24),
          alignItems: 'flex-start',
          flex: 1,
        }}
      >
        <View
          style={{
            flex: 1,
            flexDirection: 'column',
            paddingLeft: scaleOP(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
            paddingTop: scaleOP(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
          }}
        >
          {leftBlockTiles.map(({ title, content }, idx) => {
            return (
              <React.Fragment key={idx}>
                {title ? <BlockTitle scale={scaleOP} font={fontOP} C={C} title={title} /> : null}
                <View
                  style={[...styles, isWeb && shadowStyles]}
                  key={idx}
                  onLayout={(event) => {
                    const { width } = event.nativeEvent.layout;
                    setContainerWidth(width);
                  }}
                >
                  {content.map(({ i, condition, ...rest }, idx) => {
                    if (typeof condition !== 'undefined' && !condition) {
                      return null;
                    }

                    return (
                      <InputLine
                        key={i.name ?? idx}
                        scale={scaleOP}
                        font={fontOP}
                        i={i}
                        C={C}
                        bg={backgroundColor}
                        withoutStretchingForContainer
                        layoutForPhone={rest?.layoutForPhone ?? true}
                        rightSideTakesAllSpace={rest?.rightSideTakesAllSpace}
                        flexForRightColumn={1}
                        containerWidth={containerWidth}
                        {...rest}
                      />
                    );
                  })}
                </View>
              </React.Fragment>
            );
          })}
        </View>

        <View
          style={{
            flex: 1,
            width: '100%',
            flexWrap: 'nowrap',
            paddingRight: scaleOP(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
            paddingTop: scaleOP(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
          }}
        >
          {rightBlockTiles.map(({ title, component, content }, idx) => {
            return (
              <React.Fragment key={idx}>
                {title ? <BlockTitle C={C} title={title} scale={scaleOP} font={fontOP} /> : null}
                <View style={[...styles, isWeb && shadowStyles, { flex: 1 }]}>
                  {component ??
                    content.map(({ condition, i, ...rest }, idx) => {
                      if (typeof condition !== 'undefined' && !condition) {
                        return null;
                      }

                      return (
                        <InputLine
                          key={i.name ?? idx}
                          i={i}
                          C={C}
                          bg={backgroundColor}
                          scale={scaleOP}
                          font={fontOP}
                          containerWidth={containerWidth}
                          layoutForPhone={rest?.layoutForPhone ?? true}
                          rightSideTakesAllSpace={rest?.rightSideTakesAllSpace}
                          {...rest}
                          withoutStretchingForContainer
                        />
                      );
                    })}
                </View>
              </React.Fragment>
            );
          })}
        </View>
      </View>
    </ScrollView>
  );
};

export default connect(() => ({}), { operationsSearch })(ColorWrapper(JobContent));
